import React, { Component } from 'react';
import { connect } from 'react-redux';

import DLPReviews from './dlp-reviews';
import DLPSortDropdown from './dlp-sort-dropdown';
import SearchResults from '../../search-filter/components/search-results';
import ProductMap from '../../product/components/product-map';
import ProductTile from '../../product/components/product-tile';
import ProductShowPlaceholder from '../../product/components/product-show-placeholder';
import getProductProps from '../../product/get-product-props';
import useProductPrice from '../../hooks/useProductPrice';
import useAddToCart from '../../hooks/useAddToCart';
import useProductCompare from '../../hooks/useProductCompare';
import { withError } from '../../shared/components/error-boundary';
import { DLP_DEFAULT_SEARCH_OPTIONS } from '..';
import { fetchPrices } from '../../product/product-actions';
import { Helpers } from '../../core/src/helpers';
import useProductImpressions from '../../hooks/useProductImpressionsV2';
import CompareDrawer from '../../compare/components/compare-drawer';
import { attachDynamicData } from '../../search/util';
import './css/dlp-product-list.less';

const productItem =
    (addToCart, compare, prices, list) =>
    (initialProduct, key, enableAppPDP, className, productIndex, placeholder, page_level) => {
        //pass in placehodler that has price component so that prices are available earlier and can filter OOS/NO IR
        if (placeholder) {
            return <ProductShowPlaceholder product={initialProduct} />;
        }
        const [product] = attachDynamicData([initialProduct], prices) || [];

        return (
            product && (
                <ProductTile
                    className={`product-tile ${className}`}
                    key={product.sku}
                    {...getProductProps(
                        product,
                        prices[product.sku],
                        {
                            addToCart,
                            compare,
                        },
                        { resizeImage: true, hasPriceSeoText: true },
                        { list, position: (productIndex || 0) + 1 },
                    )}
                    ratingSpaceReserved={true}
                />
            )
        );
    };

const ProductMapContainer = ({ products }) => {
    const { prices } = useProductPrice(products);
    const compare = useProductCompare();
    const { addedToCart, ...addToCart } = useAddToCart(true);
    const productsWithAnalytics = useProductImpressions({
        products,
        list: 'plp',
        isEmpty: products && products.length < 1,
        componentKey: 'searchFilter',
    });

    return (
        <ProductMap
            disableMultiContainers={true}
            products={productsWithAnalytics}
            productItem={productItem(addToCart, compare, prices, 'plp')} // Analytics team requirement to use list=plp for dlps
            enableAppPDP={true}
            customPlaceHolder={true}
            v2
        />
    );
};

class DLPProductList extends Component {
    productItem = (product, key, enableAppPDP) => {
        const { disableCompare, slugKey, pg = 'test:dlp', toggleCompareDrag } = this.props;

        return (
            <ProductTile
                key={key}
                product={product}
                fetchSpecs={this.fetchTechSpecs}
                toggleCompareDrag={toggleCompareDrag}
                disableCompare={disableCompare}
                jumpInfo={{ slugKey, pg: pg.split(':').pop() }}
                enableAppPDP={enableAppPDP}
                saleCalloutPath="violatorMsg"
                hideMktDiff={true}
                {...getProductProps(
                    product,
                    prices[product.sku],
                    {
                        addToCart,
                        compare,
                    },
                    { resizeImage: true, hasPriceSeoText: true },
                    { list: page_level },
                )}
                ratingSpaceReserved={true}
            />
        );
    };

    /**
     * Fetches prices for product cards that are not yet painted on the screen (all pages except first one),
     * but are available in the SearchContainer store (fetched during SSR).
     *
     * Request will get batched with other requests originating from purchase-button.jsx components
     * into a single one (see batch.jsx, services.jsx).
     *
     * However when more product cards are mounted and their product object(s) don't have associated pricing info
     * in the redux store @state.productData.productInfo.productPrice, new batch request will be formed and sent
     * to HPServices API. Later, after I improve Swiftype indexing lambda job, this should almost never happen.
     */
    fetchPrices(results, fetchPrices, pageSize) {
        if (!(results instanceof Array) || results.length < 1) return;

        const ids = results
            .reduce((allResults, result) => allResults.concat(result), []) //just like flat()
            .slice(pageSize)
            .map(result => result.prodId);

        fetchPrices(ids);
    }

    componentDidMount() {
        const { results, fetchPrices } = this.props;
        const { pageSize } = DLP_DEFAULT_SEARCH_OPTIONS;
        //this.fetchPrices(results, fetchPrices, pageSize);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.slugKey !== this.props.slugKey) {
            // this.fetchPrices(this.props.results, this.props.fetchPrices, DLP_DEFAULT_SEARCH_OPTIONS.pageSize);
        }
    }

    getTabs = () => [
        {
            Component: Compare,
            title: 'Compare',
            active: true,
            mobileTab: () => {
                return <CompareButton />;
            },
        },
    ];

    render() {
        const { results, isComparing, leftMenuActive, pagesRevealed, enableAppPDP = true, disableCompare } = this.props;

        const { pageSize } = DLP_DEFAULT_SEARCH_OPTIONS;
        const shownResults = results.slice(0, pagesRevealed * pageSize);

        const wrapperClassList = ['dlp-product-list', leftMenuActive ? 'dlp-left-menu-open' : ''];

        return (
            <div className={wrapperClassList.join(' ')}>
                <ProductMapContainer products={shownResults} />
            </div>
        );
    }
}

const dlpProductListContainer = props => {
    return (
        <SearchResults>
            {({ results }) => (
                <React.Fragment>
                    <DLPSortDropdown match={props.match} results={results} />
                    <DLPProductList {...props} results={results} />
                    <DLPReviews {...props} results={results} />
                    <CompareDrawer />
                </React.Fragment>
            )}
        </SearchResults>
    );
};

export const mapStateToProps = state => ({
    leftMenuActive: state.leftMenu.active,
    isComparing: state.compare.products.length,
    prices: state.productData.productInfo.productPrices,
});

export const mapDispatchToProps = dispatch => ({
    fetchPrices: Helpers.memoize(ids => dispatch(fetchPrices(ids))),
});

export default connect(mapStateToProps, mapDispatchToProps)(withError(dlpProductListContainer));
